// Copyright ©️ 2026 eVolve MEP, LLC

import './assembly.css';

import type { CSSProperties, ReactNode } from 'react';

import { Card, Flex, Text, useMantineTheme } from '@mantine/core';
import { Handle, type IsValidConnection, type NodeProps, Position, useReactFlow } from '@xyflow/react';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { ImageFromFile } from 'components/Mantine/FileUploads/ImageFromFile';
import { formatQuantity } from 'helpers/formatQuantity';
import {
  maxPartNodeWidth,
  maxTaskNodeWidth,
  partNodeHeight,
  taskNodeHeight,
} from 'modules/Shop/CatalogSetup/PartEditor/nodes/constants';
import { EditNode } from 'modules/Shop/CatalogSetup/PartEditor/nodes/EditNode';
import type {
  PartNodeType,
  WriteInNodeType,
  AssemblyNodeType,
  CoreNodeType,
  TaskNodeType,
  assemblyNodeTypes,
} from 'modules/Shop/CatalogSetup/PartEditor/nodes/types';
import { CoreNodeId } from 'modules/Shop/CatalogSetup/PartEditor/types';

const handleMargin = 12;
const handleSize = 10;

const handleStyle = (position: Position): CSSProperties => ({
  width: handleSize,
  height: handleSize,
  backgroundColor: 'white',
  border: '1px solid black',
  left: position === Position.Left ? handleMargin - handleSize / 2 : undefined,
  right: position === Position.Right ? handleMargin - handleSize / 2 : undefined,
  top: position === Position.Top ? handleMargin - handleSize / 2 : undefined,
  bottom: position === Position.Bottom ? handleMargin - handleSize / 2 : undefined,
});

const labelStyle = (position: Position): CSSProperties | undefined => {
  if (position === Position.Top) {
    return {
      cursor: 'initial',
      position: 'absolute',
      top: -handleSize * 2.5,
      left: -13,
    };
  }
  if (position === Position.Bottom) {
    return {
      cursor: 'initial',
      position: 'absolute',
      bottom: -handleSize * 2.5,
      left: -16,
    };
  }
  if (position === Position.Left) {
    return {
      cursor: 'initial',
      position: 'absolute',
      left: -42,
      top: -7,
    };
  }
  // oxlint-disable-next-line typescript/no-unnecessary-condition
  if (position === Position.Right) {
    return {
      cursor: 'initial',
      position: 'absolute',
      right: -49,
      top: -7,
    };
  }
  return undefined;
};

const WrappedHandle = ({
  position,
  isValidConnection,
  showLabels,
}: {
  position: Position;
  isValidConnection: IsValidConnection;
  showLabels?: boolean;
}) => (
  <Handle
    className="hover-node--on"
    type={position === Position.Top || position === Position.Left ? 'target' : 'source'}
    position={position}
    id={position}
    style={handleStyle(position)}
    isValidConnection={isValidConnection}
  >
    {showLabels && (
      <Card px={4} py={1} radius="sm" bg="rgba(255,255,255,0.6)" style={labelStyle(position)}>
        <Text
          fz="sm"
          style={{
            wordBreak: 'initial',
          }}
        >
          {position === Position.Top || position === Position.Left ? 'Start' : 'Finish'}
        </Text>
      </Card>
    )}
  </Handle>
);

const Handles = ({
  showLabels = false,
  nodeCanBeSource = true,
  nodeCanBeTarget = true,
  children,
}: {
  children: ReactNode;
  nodeCanBeSource?: boolean;
  nodeCanBeTarget?: boolean;
  showLabels?: boolean;
}) => {
  const { getEdges } = useReactFlow();

  const isValidConnection: IsValidConnection = (params) => {
    if (params.source === params.target) return false;
    // Prevent multiple edges from the same source and the same target
    const connectionExists = getEdges().some((e) => e.source === params.source && e.target === params.target);
    return !connectionExists;
  };
  return (
    <div className="hover-node hover-title" style={{ padding: handleMargin }}>
      {children}
      {nodeCanBeTarget && (
        <>
          <WrappedHandle position={Position.Top} isValidConnection={isValidConnection} showLabels={showLabels} />
          <WrappedHandle position={Position.Left} isValidConnection={isValidConnection} showLabels={showLabels} />
        </>
      )}
      {nodeCanBeSource && (
        <>
          <WrappedHandle position={Position.Right} isValidConnection={isValidConnection} showLabels={showLabels} />
          <WrappedHandle position={Position.Bottom} isValidConnection={isValidConnection} showLabels={showLabels} />
        </>
      )}
    </div>
  );
};

const nodeIcons: Record<keyof typeof assemblyNodeTypes, EvolveIconName> = {
  'part': 'Part',
  'write-in': 'WriteInValue',
  'core': 'Assembly',
  'assembly': 'Assembly',
  'task': 'WorkCellTask',
};

export const PartNode = (node: NodeProps<PartNodeType | WriteInNodeType | AssemblyNodeType | CoreNodeType>) => {
  const {
    id: nodeId,
    type,
    data: { part, ...data },
  } = node;
  const theme = useMantineTheme();
  const colors = nodeId === CoreNodeId ? theme.colors.orange : theme.colors.gray;
  return (
    <Handles nodeCanBeSource={false} nodeCanBeTarget={nodeId === CoreNodeId}>
      <Card
        p={0}
        withBorder
        bg={colors[0]}
        style={{
          height: partNodeHeight,
          minWidth: 250,
          maxWidth: maxPartNodeWidth,
          borderColor: colors[6],
          boxSizing: 'border-box',
        }}
      >
        <Flex>
          <Card
            p={0}
            withBorder
            display="flex"
            style={{
              width: partNodeHeight,
              height: partNodeHeight,
              minWidth: partNodeHeight,
              minHeight: partNodeHeight,
              objectFit: 'contain',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ImageFromFile
              fileId={'image' in part ? part.image?.fileId : undefined}
              alt={part.name}
              fallback={<EvolveIcon size="xl" color="gray.7" icon={nodeIcons[type as keyof typeof nodeIcons]} />}
              size={partNodeHeight}
            />
          </Card>
          <Flex direction="column" justify="space-between" style={{ flex: '1 1 auto' }}>
            <Flex direction="column" px="sm" justify="center" fz="sm" style={{ flex: '1 1 auto' }}>
              <Text lineClamp={1} title={part.name} fw={500}>
                {part.name}
              </Text>
              {!!part.description && (
                <Text lineClamp={1} fz="xs" title={part.description}>
                  {part.description}
                </Text>
              )}
              {nodeId !== CoreNodeId && <EditNode node={node as NodeProps<PartNodeType>} />}
            </Flex>
            <Flex bg="gray.1" gap="sm" fz="xs" px="sm" py={4} c="dimmed">
              {nodeId !== CoreNodeId && 'quantity' in data && <Text>Unit Qty: {formatQuantity(data.quantity)}</Text>}
              <Text>UOM: {part.unitOfMeasureCode}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </Handles>
  );
};

export const TaskNode = ({
  data: {
    taskType: { taskTypeName, taskTypeDescription, imageFileId },
  },
}: NodeProps<TaskNodeType>) => {
  const theme = useMantineTheme();
  return (
    <Handles showLabels>
      <Card
        withBorder
        bg="violet.1"
        p={0}
        style={{
          height: taskNodeHeight,
          minWidth: 200,
          maxWidth: maxTaskNodeWidth,
          borderColor: theme.colors.violet[6],
          boxSizing: 'border-box',
        }}
      >
        <Flex>
          <Flex
            bg="white"
            align="center"
            justify="center"
            style={{ width: taskNodeHeight, height: taskNodeHeight, minWidth: taskNodeHeight }}
          >
            <ImageFromFile
              fileId={imageFileId}
              size={taskNodeHeight}
              fallback={<EvolveIcon icon={nodeIcons['task']} color="violet.8" />}
            />
          </Flex>
          <Flex px="sm" direction="column" justify="space-evenly" style={{ overflow: 'hidden', userSelect: 'none' }}>
            <AgGridStyleTooltip label={taskTypeName} position="top-start">
              <Text fz="sm" c="violet.8">
                {taskTypeName}
              </Text>
            </AgGridStyleTooltip>
            {!!taskTypeDescription && (
              <AgGridStyleTooltip label={taskTypeDescription} position="top-start">
                <Text fz="xs" c="violet.9">
                  {taskTypeDescription}
                </Text>
              </AgGridStyleTooltip>
            )}
          </Flex>
        </Flex>
      </Card>
    </Handles>
  );
};
